import { IUsername } from '@customTypes/user';

export const getPatientName = (name?: IUsername): string => {
  if (!name) return '';

  return name.first + ' ' + name.last;
};

export type ProductNameSource = {
  product_name?: string;
  product_family_name?: string | null;
  product_test_name?: string | null;
  product_test_type?: string | null;
  product_test_subclass?: string | null;
};

type OrderTestName = {
  brand?: string;
  productLine?: string;
  productName?: string;
};

const removeLeadingMyOme = (name: string): string =>
  name
    .trim()
    .replace(/^myome\b(?:,\s*|\s*)/i, '')
    .trim();

export const getOrderTestName = (product: ProductNameSource): OrderTestName => {
  const familyName = product.product_family_name?.trim();
  const testName = product.product_test_name?.trim();

  if (familyName && testName) {
    const isMyOme = /^myome\b/i.test(familyName);

    return {
      ...(isMyOme ? { brand: 'MyOme' } : {}),
      productLine: removeLeadingMyOme(familyName),
      productName: testName,
    };
  }

  return {
    productLine: removeLeadingMyOme(familyName || product.product_name || ''),
  };
};

export type PatientOrderTestDisplayLabels = {
  /** Primary title (matches first line in PatientDetailOrder/expanded/PatientDetailOrderCard). */
  headline: string;
  /** Secondary muted line; omitted when headline is the product line only. */
  subline?: string;
  /** Whether to render the headline row (same condition as the card’s first block). */
  showHeadline: boolean;
};

export const getOrderTestDisplayLabels = (order: ProductNameSource): PatientOrderTestDisplayLabels => {
  const testName = getOrderTestName(order);
  const useProductLineAsHeadline = !testName.productName && !!testName.productLine;

  if (useProductLineAsHeadline) {
    return {
      headline: testName.productLine ?? '',
      showHeadline: true,
    };
  }

  return {
    headline: testName.productName ?? testName.productLine ?? '',
    subline: testName.productLine || undefined,
    showHeadline: !!(testName.productName || testName.productLine),
  };
};
